import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from 'shared/components/Icon';
import {
  WHO_TIME_UNITS,
  DEFAULT_WHO_TIME_FILTER,
  formatWhoTimeFilterLabel,
  computeWhoTimeRange,
} from 'features/activity/utils/whoTimeFilterUtils';

const MODES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'last', label: 'Last' },
  { value: 'since', label: 'Since' },
];

/**
 * Dropdown for WHO event date-time filter (Fixed / Last / Since).
 * Stored as eventTimeFilter on the filter object.
 */
const WhoTimeFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [local, setLocal] = useState(() => ({
    ...DEFAULT_WHO_TIME_FILTER,
    ...value,
  }));

  useEffect(() => {
    if (isOpen) {
      setLocal({ ...DEFAULT_WHO_TIME_FILTER, ...value });
    }
  }, [isOpen, value]);
  const buttonRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const tf = { ...DEFAULT_WHO_TIME_FILTER, ...value };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) });
    }
  }, [isOpen]);

  const handleApply = () => {
    const range = computeWhoTimeRange(local);
    if (range || local.mode === 'last') {
      onChange({ ...local });
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setIsOpen((o) => !o); }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-surface-hover rounded-lg text-[color:var(--text-primary)] focus:outline-none focus:ring-0 cursor-pointer hover:bg-surface-hover transition-colors"
        title="Event date range"
      >
        <Icon name="calendar" size={14} color="var(--text-muted)" />
        <span>{formatWhoTimeFilterLabel(tf)}</span>
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              className="fixed z-[9999] bg-surface border border-[color:var(--border-default)] rounded-lg shadow-xl p-4"
              style={{ top: `${pos.top}px`, left: `${pos.left}px`, width: `${Math.max(pos.width, 280)}px`, fontFamily: 'Inter, sans-serif' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-2 mb-3">
                {MODES.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setLocal((prev) => ({ ...prev, mode: m.value }))}
                    className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                      local.mode === m.value ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand' : 'bg-surface-hover text-[color:var(--text-muted)] hover:bg-surface-hover'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {local.mode === 'fixed' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[color:var(--text-muted)] mb-1">Start date</label>
                    <input
                      type="date"
                      value={local.startDate || ''}
                      onChange={(e) => setLocal((prev) => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-2 py-1.5 text-xs border border-line-strong rounded focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[color:var(--text-muted)] mb-1">End date</label>
                    <input
                      type="date"
                      value={local.endDate || ''}
                      onChange={(e) => setLocal((prev) => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-2 py-1.5 text-xs border border-line-strong rounded focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
                    <input
                      type="checkbox"
                      checked={!!local.enableTimeRanges}
                      onChange={(e) => setLocal((prev) => ({
                        ...prev,
                        enableTimeRanges: e.target.checked,
                        startTime: e.target.checked ? (prev.startTime || '00:00') : undefined,
                        endTime: e.target.checked ? (prev.endTime || '23:59') : undefined,
                      }))}
                      className="h-4 w-4 shrink-0 rounded border-line-strong accent-[color:var(--trodo-primary)] focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0"
                    />
                    Include time
                  </label>
                  {local.enableTimeRanges && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-[color:var(--text-muted)] mb-1">Start</label>
                        <input
                          type="time"
                          value={local.startTime || '00:00'}
                          onChange={(e) => setLocal((prev) => ({ ...prev, startTime: e.target.value }))}
                          className="w-full px-2 py-1.5 text-xs border border-line-strong rounded"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-[color:var(--text-muted)] mb-1">End</label>
                        <input
                          type="time"
                          value={local.endTime || '23:59'}
                          onChange={(e) => setLocal((prev) => ({ ...prev, endTime: e.target.value }))}
                          className="w-full px-2 py-1.5 text-xs border border-line-strong rounded"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {local.mode === 'last' && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={local.lastAmount ?? 180}
                    onChange={(e) => setLocal((prev) => ({ ...prev, lastAmount: parseInt(e.target.value, 10) || 1 }))}
                    className="w-16 px-2 py-1.5 text-xs border border-line-strong rounded"
                  />
                  <select
                    value={local.lastUnit || 'days'}
                    onChange={(e) => setLocal((prev) => ({ ...prev, lastUnit: e.target.value }))}
                    className="flex-1 px-2 py-1.5 text-xs border border-line-strong rounded"
                  >
                    {WHO_TIME_UNITS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {local.mode === 'since' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[color:var(--text-muted)] mb-1">From date</label>
                    <input
                      type="date"
                      value={local.sinceDate || ''}
                      onChange={(e) => setLocal((prev) => ({ ...prev, sinceDate: e.target.value }))}
                      className="w-full px-2 py-1.5 text-xs border border-line-strong rounded focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
                    <input
                      type="checkbox"
                      checked={!!local.enableTimeRanges}
                      onChange={(e) => setLocal((prev) => ({
                        ...prev,
                        enableTimeRanges: e.target.checked,
                        sinceTime: e.target.checked ? (prev.sinceTime || '00:00') : undefined,
                      }))}
                      className="h-4 w-4 shrink-0 rounded border-line-strong accent-[color:var(--trodo-primary)] focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0"
                    />
                    Include time
                  </label>
                  {local.enableTimeRanges && (
                    <div>
                      <label className="block text-xs text-[color:var(--text-muted)] mb-1">From time</label>
                      <input
                        type="time"
                        value={local.sinceTime || '00:00'}
                        onChange={(e) => setLocal((prev) => ({ ...prev, sinceTime: e.target.value }))}
                        className="w-full px-2 py-1.5 text-xs border border-line-strong rounded"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[color:var(--border-default)]">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-3 py-1.5 text-xs text-[color:var(--text-muted)] hover:bg-surface-hover rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="px-3 py-1.5 text-xs font-medium text-ink-onBrand bg-[color:var(--trodo-primary)] rounded hover:bg-[color:var(--trodo-primary-hover)]"
                >
                  Apply
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default WhoTimeFilterDropdown;
